/**
 * Local recovery copy for an in-progress worksheet attempt (plan §8, Task 11).
 * Kept in `localStorage`, namespaced by user/assignment/revision and expired after
 * seven days. It stores only the student's own answers plus the stable
 * `clientAttemptId` — never an answer key — so a reload can resume the same server
 * attempt. The copy is cleared on logout or a successful submit.
 */
import type { WorksheetAnswers } from '../renderer/types';

const PREFIX = 'worksheet-attempt-recovery';
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export type WorksheetRecoveryRecord = {
  clientAttemptId: string;
  answers: WorksheetAnswers;
  /** Draft version the local answers were based on; -1 means "not yet known". */
  serverVersion: number;
  updatedAt: number;
};

const storage = (): Storage | null =>
  typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;

export function worksheetRecoveryKey(
  userId: string | number | null,
  assignmentCode: string,
  revisionId: number | null,
): string {
  return `${PREFIX}::${userId ?? 'anon'}::${assignmentCode}::${revisionId ?? 'unknown'}`;
}

/** Loads a non-expired recovery record, removing it when it is stale or malformed. */
export function loadWorksheetRecovery(
  key: string,
  now: number = Date.now(),
): WorksheetRecoveryRecord | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WorksheetRecoveryRecord;
    if (!parsed || typeof parsed.clientAttemptId !== 'string' || parsed.clientAttemptId === '') {
      store.removeItem(key);
      return null;
    }
    if (typeof parsed.updatedAt !== 'number' || now - parsed.updatedAt > EXPIRY_MS) {
      store.removeItem(key);
      return null;
    }
    return { ...parsed, answers: parsed.answers ?? {} };
  } catch {
    store.removeItem(key);
    return null;
  }
}

export function saveWorksheetRecovery(key: string, record: WorksheetRecoveryRecord): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(record));
  } catch {
    // Storage full or disabled: recovery is best-effort, the screen state remains.
  }
}

export function clearWorksheetRecovery(key: string): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // Ignore: nothing else can be done if storage is unavailable.
  }
}

/** Removes every recovery copy belonging to one user (logout). */
export function clearWorksheetAttemptRecoveryForUser(userId: string | number | null): void {
  const store = storage();
  if (!store) return;
  const marker = `${PREFIX}::${userId ?? 'anon'}::`;
  const keys: string[] = [];
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key && key.startsWith(marker)) keys.push(key);
  }
  keys.forEach((key) => store.removeItem(key));
}

/** Removes every worksheet recovery copy on this device (logout of any user). */
export function clearAllWorksheetAttemptRecovery(): void {
  const store = storage();
  if (!store) return;
  const keys: string[] = [];
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key && key.startsWith(PREFIX)) keys.push(key);
  }
  keys.forEach((key) => store.removeItem(key));
}

/** Stable attempt id: a UUID reused by every retry of the same attempt. */
export function createClientAttemptId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `attempt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
